import { useEffect } from 'react';
import { persistGridState, type GridColumnWidthModel } from './gridState';
import type {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

type UseGridPersistenceParams = {
  baseStorageKey: string | null;
  organisationId?: string | null;
  projectId?: string | null;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnWidthModel: GridColumnWidthModel;
};

export const useGridPersistence = ({
  baseStorageKey,
  organisationId,
  projectId,
  paginationModel,
  sortModel,
  filterModel,
  columnVisibilityModel,
  columnWidthModel,
}: UseGridPersistenceParams): void => {
  useEffect(() => {
    if (!baseStorageKey) return;
    try {
      const storageKey = baseStorageKey;
      const persistedOrg = localStorage.getItem(`${storageKey}:org`);
      const persistedProject = localStorage.getItem(`${storageKey}:project`);
      const currentOrgKey = (organisationId ?? 'personal') as string;
      const currentProjectKey = (projectId ?? 'all-projects') as string;

      const orgChanged = persistedOrg && persistedOrg !== currentOrgKey;
      const projectChanged = persistedProject && persistedProject !== currentProjectKey;
      if (orgChanged || projectChanged) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}:org`);
        localStorage.removeItem(`${storageKey}:project`);
      }
      localStorage.setItem(`${storageKey}:org`, currentOrgKey);
      localStorage.setItem(`${storageKey}:project`, currentProjectKey);
    } catch {
      // ignore storage failures
    }
  }, [baseStorageKey, organisationId, projectId]);

  useEffect(() => {
    if (!baseStorageKey) return;
    persistGridState(
      baseStorageKey,
      { page: paginationModel.page, pageSize: paginationModel.pageSize },
      sortModel,
      filterModel,
      columnVisibilityModel,
      columnWidthModel,
    );
  }, [
    baseStorageKey,
    columnVisibilityModel,
    columnWidthModel,
    filterModel,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
  ]);
};
